import { STATIC_URL } from '@/config/config.js';
import { api } from '@/services/api.js';
import authService, { ROLE_NAMES, USER_ROLES, VOICE_TYPE_NAMES } from '@/services/authService.js';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useAuthStore = defineStore('auth', () => {
  // 状态
  const user = ref(null);
  const isLoading = ref(false);
  const error = ref(null);
0
  // 计算属性
  const isAuthenticated = computed(() => !!user.value);
  const userRole = computed(() => user.value?.role || null);
  const userRoleName = computed(() => user.value ? ROLE_NAMES[user.value.role] || '未知' : '未登录');
  const userVoiceTypeName = computed(() => user.value ? VOICE_TYPE_NAMES[user.value.vType] || '未设置' : '未登录');
  const isAdmin = computed(() => userRole.value === USER_ROLES.ADMIN);
  const isTeacher = computed(() => userRole.value === USER_ROLES.TEACHER);
  const isStudent = computed(() => userRole.value === USER_ROLES.STUDENT);
  const isJudge = computed(() => false);
  const token = computed(() => authService.getAccessToken());

  // 初始化用户状态
  const initializeAuth = () => {
    const storedUser = authService.getUser();
    const token = authService.getAccessToken();
    
    if (storedUser && token) {
      user.value = storedUser;
    }
  };

  // 用户注册
  const register = async (userData) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await authService.register(userData);
      
      if (response.success) {
        user.value = response.data.user;
        return { success: true, data: response.data };
      }
      
      throw new Error(response.message || '注册失败');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // 用户登录
  const login = async (credentials) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await authService.login(credentials);
      
      if (response.success) {
        user.value = response.data.user;
        return { success: true, data: response.data };
      }
      
      throw new Error(response.message || '登录失败');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // 获取用户信息（强制从服务器获取最新数据，不使用缓存）
  const fetchProfile = async (forceRefresh = true) => {
    try {
      isLoading.value = true;
      error.value = null;

      console.log('fetchProfile 开始获取用户信息，强制刷新:', forceRefresh);

      const response = await authService.getProfile();

      console.log('fetchProfile 收到响应:', response);

      // API服务已经处理了axios响应，直接使用response
      if (response && response.success) {
        // 强制更新用户数据，不使用任何缓存
        user.value = { ...response.data.user };
        console.log('fetchProfile 用户信息已更新:', user.value);
        return user.value;
      }

      throw new Error(response?.message || '获取用户信息失败');
    } catch (err) {
      // 保留原始错误信息，不要覆盖
      if (err.response?.data?.message) {
        error.value = err.response.data.message;
      } else {
        error.value = err.message;
      }
      
      console.error('fetchProfile 获取用户信息失败:', err);
      
      // 检查是否是账户待审核错误
      if (err.response?.data?.code === 'ACCOUNT_PENDING') {
        // 创建一个包含完整错误信息的错误对象
        const pendingError = new Error(err.response.data.message || '账户待审核，请联系管理员激活账户');
        pendingError.response = err.response;
        pendingError.code = err.response.data.code;
        throw pendingError;
      }
      
      // 如果获取用户信息失败，可能是token过期，清除本地状态
      if (err.code === 401 || err.response?.status === 401) {
        logout();
      }
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // 获取用户信息的别名方法
  const fetchUserInfo = fetchProfile;

  // 更新用户信息
  const updateProfile = async (userData) => {
    try {
      isLoading.value = true;
      error.value = null;

      console.log('updateProfile 开始更新用户信息:', userData);

      const response = await authService.updateProfile(userData);

      console.log('updateProfile 收到响应:', response);

      if (response && response.success) {
        // 更新成功后强制重新获取最新的用户信息
        console.log('updateProfile 更新成功，重新获取用户信息');
        await fetchProfile(true);
        return response.data;
      }

      throw new Error(response?.message || '更新用户信息失败');
    } catch (err) {
      error.value = err.message;
      console.error('updateProfile 更新失败:', err);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // 更新用户头像
  const updateUserAvatar = async (avatarPath) => {
    try {
      console.log('updateUserAvatar 开始更新头像:', avatarPath);

      // 调用后端API更新头像
      const response = await api.put('/auth/profile', { avatarPath: avatarPath });

      console.log('updateUserAvatar 收到响应:', response);

      if (response && response.success) {
        // 更新成功后强制重新获取最新的用户信息
        console.log('updateUserAvatar 头像更新成功，重新获取用户信息');
        await fetchProfile(true);
        return response.data;
      } else {
        throw new Error(response?.message || '更新头像失败');
      }
    } catch (err) {
      console.error('更新头像失败:', err);
      throw err;
    }
  };

  // 更新用户个人信息（已废弃，使用updateProfile代替）
  const updateUserProfile = async (profileData) => {
    console.warn('updateUserProfile已废弃，请使用updateProfile方法');
    // 这个方法已经不再使用，保留只是为了兼容性
  };

  // 修改密码
  const changePassword = async (passwordData) => {
    try {
      isLoading.value = true;
      error.value = null;

      const response = await authService.changePassword(passwordData);
      
      if (response.success) {
        return response;
      }
      
      throw new Error(response.message || '修改密码失败');
    } catch (err) {
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // 用户登出
  const logout = () => {
    user.value = null;
    error.value = null;
    authService.logout();
  };

  // 清除错误
  const clearError = () => {
    error.value = null;
  };

  // 检查权限
  const hasPermission = (requiredRole) => {
    if (!user.value) return false;
    
    const currentRole = Number(user.value.role);
    
    // 管理员拥有所有权限
    if (currentRole === USER_ROLES.ADMIN) return true;
    
    // 检查特定角色权限
    if (Array.isArray(requiredRole)) {
      return requiredRole.includes(currentRole);
    }
    
    return currentRole === requiredRole;
  };

  // 检查是否为资源所有者
  const isResourceOwner = (resourceUserId) => {
    return user.value && user.value.id === resourceUserId;
  };

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (!user.value) return '未登录用户';
    return user.value.name || user.value.email || '用户';
  };

  // 获取用户头像URL，添加时间戳防止缓存
  const getUserAvatarUrl = () => {
    if (!user.value || !user.value.avatarPath) {
      return '/default-avatar.png';
    }

    const timestamp = Date.now();

    if (user.value.avatarPath.startsWith('http')) {
      const url = new URL(user.value.avatarPath);
      url.searchParams.set('t', timestamp.toString());
      return url.toString();
    } else {
      return `${STATIC_URL}${user.value.avatarPath}?t=${timestamp}`;
    }
  };

  return {
    // 状态
    user,
    isLoading,
    error,
    
    // 计算属性
    isAuthenticated,
    userRole,
    userRoleName,
    userVoiceTypeName,
    isAdmin,
    isTeacher,
    isStudent,
    token,
    
    // 方法
    initializeAuth,
    register,
    login,
    fetchProfile,
    fetchUserInfo,
    updateProfile,
    updateUserAvatar,
    updateUserProfile,
    changePassword,
    logout,
    clearError,
    hasPermission,
    isResourceOwner,
    getUserDisplayName,
    getUserAvatarUrl
  };
});
